import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { DocumentUpload } from '../../components/ingestion/DocumentUpload';
import { JobsList } from '../../components/ingestion/JobsList';
import WhatsAppTab from '../../components/knowledge/WhatsAppTab';
import { ingestionService, BuildRagStatus } from '../../services/ingestionService';
import { whatsappService, WhatsAppConversation } from '../../services/whatsappService';
import { usePermissions } from '../../hooks/usePermissions';
import { ServerStackIcon, ArrowPathIcon, PlusCircleIcon } from '@heroicons/react/24/outline';

// last_result is "<kind>: <message for the admin>"
const resultMessage = (lastResult: string) => lastResult.replace(/^\w+:\s*/, '');

export default function KnowledgeBasePage() {
  const { isGod } = usePermissions();
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [ragBuilding, setRagBuilding] = useState(false);
  const [ragMode, setRagMode] = useState<'incremental' | 'rebuild' | null>(null);
  const [ragInitializing, setRagInitializing] = useState(true);
  const [ragStatus, setRagStatus] = useState<BuildRagStatus | null>(null);
  const [pendingWhatsApp, setPendingWhatsApp] = useState<WhatsAppConversation[]>([]);
  const [pendingDocsCount, setPendingDocsCount] = useState(0);

  const ragPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ragModeRef = useRef<'incremental' | 'rebuild' | null>(null);
  const ragPollingStartRef = useRef<number | null>(null);
  const MAX_POLL_MS = 2 * 60 * 60 * 1000; // 2 hours

  // Cleanup rag polling interval on unmount
  useEffect(() => {
    return () => {
      if (ragPollingRef.current) {
        clearInterval(ragPollingRef.current);
        ragPollingRef.current = null;
      }
    };
  }, []);

  const handleUploadComplete = () => {
    setRefreshTrigger(prev => prev + 1);
    loadPendingDocs();
  };

  const fetchRagStatus = useCallback(async () => {
    try {
      const status = await ingestionService.getBuildRagStatus();
      setRagStatus(status);
      setRagBuilding(status.running);
      if (status.running) {
        // After a page reload, show the mode that was actually triggered
        const mode = status.force_rebuild ? 'rebuild' : 'incremental';
        ragModeRef.current = mode;
        setRagMode(mode);
      }
    } catch {
      // Silently fail - status endpoint may not be available
    } finally {
      setRagInitializing(false);
    }
  }, []);

  const loadPendingWhatsApp = useCallback(async () => {
    try {
      const response = await whatsappService.getPendingIndexing();
      setPendingWhatsApp(response.conversations || []);
    } catch {
      setPendingWhatsApp([]);
    }
  }, []);

  const loadPendingDocs = useCallback(async () => {
    try {
      const response = await ingestionService.listJobs(1, 50);
      setPendingDocsCount(response.jobs.filter(j => j.status === 'uploaded').length);
    } catch {
      setPendingDocsCount(0);
    }
  }, []);

  useEffect(() => {
    fetchRagStatus();
    loadPendingWhatsApp();
    loadPendingDocs();
  }, [fetchRagStatus, loadPendingWhatsApp, loadPendingDocs]);

  useEffect(() => {
    if (!ragBuilding) return;

    // Guard: clear any existing interval before starting a new one
    if (ragPollingRef.current) {
      clearInterval(ragPollingRef.current);
      ragPollingRef.current = null;
    }

    ragPollingStartRef.current = Date.now();
    ragPollingRef.current = setInterval(async () => {
      // Safety valve: stop if polling for more than 2 hours
      if (ragPollingStartRef.current && Date.now() - ragPollingStartRef.current > MAX_POLL_MS) {
        clearInterval(ragPollingRef.current!);
        ragPollingRef.current = null;
        ragPollingStartRef.current = null;
        setRagBuilding(false);
        setRagMode(null);
        ragModeRef.current = null;
        toast.error('Tempo limite de acompanhamento atingido', {
          description: 'O pipeline pode ainda estar rodando no servidor. Recarregue a página para verificar o status.',
        });
        return;
      }
      try {
        const status = await ingestionService.getBuildRagStatus();
        setRagStatus(status);
        if (!status.running) {
          // Stop the interval immediately to prevent double-firing
          clearInterval(ragPollingRef.current!);
          ragPollingRef.current = null;
          ragPollingStartRef.current = null;
          setRagBuilding(false);

          if (status.last_result === 'success') {
            // Backend already marks docs and WhatsApp conversations as indexed — just reload UI state
            await loadPendingWhatsApp();
            await loadPendingDocs();
            setRefreshTrigger(prev => prev + 1);
            if (ragModeRef.current === 'incremental') {
              toast.success('Novos documentos indexados com sucesso!', {
                description: 'Apenas os arquivos ainda não indexados foram adicionados ao RAG.',
              });
            } else {
              toast.success('Base de dados RAG reconstruída com sucesso!', {
                description: 'Base flat RAG recriada com todos os documentos.',
              });
            }
          } else if (status.last_result?.startsWith('partial')) {
            await loadPendingWhatsApp();
            await loadPendingDocs();
            setRefreshTrigger(prev => prev + 1);
            toast.warning('Indexação concluída com pendências', {
              description: resultMessage(status.last_result),
            });
          } else if (status.last_result?.startsWith('error')) {
            setRefreshTrigger(prev => prev + 1);
            toast.error('A indexação não foi concluída', {
              description: resultMessage(status.last_result),
            });
          }
          ragModeRef.current = null;
          setRagMode(null);
        }
      } catch {
        // ignore polling errors
      }
    }, 5000);

    return () => {
      if (ragPollingRef.current) {
        clearInterval(ragPollingRef.current);
        ragPollingRef.current = null;
      }
    };
    // pendingWhatsApp.length intentionally excluded — accessed via ref to prevent interval duplication
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ragBuilding, loadPendingWhatsApp, loadPendingDocs]);

  const handleIndexNew = async () => {
    ragModeRef.current = 'incremental';
    setRagMode('incremental');
    try {
      const response = await ingestionService.buildRag(false);
      setRagBuilding(true);
      toast.info('Indexação incremental iniciada', {
        description: response.message,
      });
    } catch (error: any) {
      ragModeRef.current = null;
      setRagMode(null);
      const detail = error?.response?.data?.detail || error?.message || 'Erro ao iniciar indexação';
      toast.error('Erro ao iniciar indexação', { description: detail });
    }
  };

  const handleBuildRag = async () => {
    ragModeRef.current = 'rebuild';
    setRagMode('rebuild');
    try {
      const response = await ingestionService.buildRag(true);
      setRagBuilding(true);
      toast.info('Reconstrução iniciada', {
        description: response.message,
      });
    } catch (error: any) {
      ragModeRef.current = null;
      setRagMode(null);
      const detail = error?.response?.data?.detail || error?.message || 'Erro ao iniciar pipeline';
      toast.error('Erro ao iniciar reconstrução', {
        description: detail,
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Base de Conhecimento</h1>
        <p className="mt-2 text-sm text-gray-700">
          Gerencie documentos e conversas do WhatsApp para alimentar a base de conhecimento do assistente IA
        </p>
      </div>

      {/* Section 1: Documents */}
      <section className="space-y-6">
        <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
          Documentos
        </h2>
        <DocumentUpload onUploadComplete={handleUploadComplete} />
        <JobsList refreshTrigger={refreshTrigger} />
      </section>

      <hr className="border-gray-200" />

      {/* Section 2: WhatsApp */}
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
          WhatsApp
        </h2>
        <WhatsAppTab />
      </section>

      <hr className="border-gray-200" />

      {/* Section 3: RAG Build */}
      <section>
        <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-6">
          Construção da Base RAG
        </h2>
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <ServerStackIcon className="h-5 w-5 text-gray-500" />
              <h3 className="text-base font-medium text-gray-900">Indexação da Base</h3>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Adiciona à base de conhecimento os documentos e as conversas do WhatsApp extraídas.
            </p>
          </div>

          <div className="p-6 space-y-4">
            {(pendingDocsCount > 0 || pendingWhatsApp.length > 0) && (
              <div className="rounded-md bg-indigo-50 p-3 space-y-1">
                {pendingDocsCount > 0 && (
                  <p className="text-sm text-indigo-700">
                    <strong>{pendingDocsCount}</strong> documento(s) aguardando indexação no RAG.
                  </p>
                )}
                {pendingWhatsApp.length > 0 && (
                  <p className="text-sm text-indigo-700">
                    <strong>{pendingWhatsApp.length}</strong> conversa(s) do WhatsApp com conhecimento extraído pendente de indexação.
                  </p>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={handleIndexNew}
              disabled={ragBuilding || ragInitializing}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold shadow-sm transition-colors ${ragBuilding || ragInitializing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                }`}
            >
              {ragBuilding && ragMode === 'incremental' ? (
                <ArrowPathIcon className="h-5 w-5 animate-spin" />
              ) : (
                <PlusCircleIcon className="h-5 w-5" />
              )}
              {ragInitializing ? 'Verificando...' : (ragBuilding && ragMode === 'incremental') ? 'Indexando...' : 'Indexar Novos Documentos'}
            </button>

            <p className="text-xs text-gray-500 text-center">
              Adiciona apenas arquivos ainda não indexados — mais rápido e não apaga dados existentes.
            </p>

            {/* Full rebuild wipes the collection — restricted to god (also enforced by the API) */}
            {isGod() && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-2 text-xs text-gray-400">ou</span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleBuildRag}
                  disabled={ragBuilding || ragInitializing}
                  className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-3 text-sm font-semibold shadow-sm transition-colors ${ragBuilding || ragInitializing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-orange-600 text-white hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600'
                    }`}
                >
                  {ragBuilding && ragMode === 'rebuild' ? (
                    <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  ) : (
                    <ServerStackIcon className="h-5 w-5" />
                  )}
                  {ragInitializing ? 'Verificando...' : (ragBuilding && ragMode === 'rebuild') ? 'Reconstruindo...' : 'Reconstruir Base Completa'}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Apaga a base existente e reconstrói do zero com todos os documentos — operação lenta, consome tokens OpenAI.
                </p>
              </>
            )}

            {ragStatus?.last_run && !ragBuilding && (
              <p className="text-xs text-gray-400 text-center">
                Última execução: {new Date(ragStatus.last_run).toLocaleString('pt-BR')}
                {ragStatus.last_result === 'success' && ' — Sucesso'}
                {ragStatus.last_result?.startsWith('partial') && ' — Concluída com pendências'}
                {ragStatus.last_result?.startsWith('error') && ' — Não concluída'}
              </p>
            )}

            {ragInitializing && (
              <div className="rounded-md bg-gray-50 p-3">
                <p className="text-sm text-gray-500">
                  Verificando status do pipeline...
                </p>
              </div>
            )}

            {ragBuilding && !ragInitializing && (
              <div className="rounded-md bg-blue-50 p-3">
                <p className="text-sm text-blue-700">
                  {ragMode === 'incremental'
                    ? 'Indexando novos documentos. Isso pode levar alguns instantes...'
                    : 'Reconstruindo a base completa. Isso pode levar alguns minutos dependendo do volume de dados...'}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
